import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { MessageList } from '@/components/message-list';
import { MessageView } from '@/components/message-view';
import { SentRoute } from '@/routes/sent';
import { RecentlyDeletedRoute } from '@/routes/recently-deleted';
import { useMailStore } from '@/stores/mail';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { SweepModal } from '@/components/sweep-modal';

export function InboxRoute() {
  const { mailboxId, uid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const folder = searchParams.get('folder') || 'inbox';
  // IMAP folder the selected row lives in (e.g. [Gmail]/Spam) — UIDs collide across folders.
  const imapFolder = searchParams.get('in');
  const { selectedMailbox, setSelectedMailbox, mailboxes } = useMailStore();
  const [selectedMessageUid, setSelectedMessageUid] = useState<number | null>(
    uid ? parseInt(uid, 10) : null
  );
  const [showSweep, setShowSweep] = useState(false);

  useEffect(() => {
    if (mailboxId) {
      const m = mailboxes.find((x) => x.id === mailboxId) || null;
      setSelectedMailbox(m);
    }
  }, [mailboxId, mailboxes, setSelectedMailbox]);

  useEffect(() => {
    setSelectedMessageUid(uid ? parseInt(uid, 10) : null);
  }, [uid]);

  const activeMailboxId = mailboxId || selectedMailbox?.id || null;
  const hasSelectedMessage = selectedMessageUid !== null;

  // Map folder query param to the action filter the API expects.
  // The Inbox must show CLEAN mail only — review/spam live in their own folders.
  // (Previously the inbox passed no filter, so spam/review leaked into the inbox.)
  const actionFilter = folder === 'maybe-spam' ? 'review'
    : folder === 'spam' ? 'spam'
    : folder === 'inbox' ? 'clean'
    : undefined;

  // Sent / Deleted are single-list views, filtered to this mailbox.
  if (folder === 'sent') {
    return <SentRoute mailboxId={activeMailboxId || undefined} />;
  }
  if (folder === 'deleted') {
    return <RecentlyDeletedRoute mailboxId={activeMailboxId || undefined} />;
  }

  return (
    <div className="flex h-full w-full">
      {/* Message list — full width on mobile when no message selected, 1/3 on desktop */}
      <div className={`
        flex flex-col border-r border-bmail-border bg-bmail-surface
        ${hasSelectedMessage ? 'hidden md:flex md:w-1/3 md:min-w-[20rem] md:max-w-md' : 'flex w-full md:w-1/3 md:min-w-[20rem] md:max-w-md'}
      `}>
        <MessageList mailboxId={activeMailboxId} selectedUid={selectedMessageUid} selectedImapFolder={imapFolder} folder={folder} action={actionFilter} onSweep={() => setShowSweep(true)} />
      </div>

      {/* Message view — full width on mobile when message selected, 2/3 on desktop */}
      <div className={`
        flex flex-col bg-[var(--bg)]
        ${hasSelectedMessage ? 'flex w-full md:flex-1' : 'hidden md:flex md:flex-1'}
      `}>
        {/* Mobile back button */}
        {hasSelectedMessage && (
          <button
            onClick={() => navigate(`/inbox/${activeMailboxId || ''}${folder !== 'inbox' ? `?folder=${folder}` : ''}`)}
            className="flex items-center gap-1 border-b border-bmail-border px-4 py-2 text-sm text-bmail-text-secondary hover:text-bmail-text md:hidden"
          >
            <ArrowLeft size={16} /> Back to {folder === 'inbox' ? 'inbox' : folder}
          </button>
        )}
        <MessageView
          mailboxId={activeMailboxId}
          uid={selectedMessageUid}
          folder={imapFolder}
          onActioned={() => navigate(`/inbox/${activeMailboxId || ''}${folder !== 'inbox' ? `?folder=${folder}` : ''}`)}
        />
      </div>

      {/* Sweep modal — scoped to current mailbox + folder. Virtual folders
          (maybe-spam / spam) pass a spamAction scope instead of an IMAP folder
          so the unsubscribe sweep targets the list you're viewing. */}
      {showSweep && activeMailboxId && (
        <SweepModal
          mailboxId={activeMailboxId}
          mailboxEmail={selectedMailbox?.email}
          folder={folder === 'inbox' ? 'INBOX' : folder === 'maybe-spam' || folder === 'spam' ? undefined : folder}
          spamAction={folder === 'maybe-spam' ? 'review' : folder === 'spam' ? 'spam' : undefined}
          onClose={() => setShowSweep(false)}
        />
      )}
    </div>
  );
}